import type { HoleGeometry, HoleScore, LatLng, PlateImagery, Shot } from './golfRound'
import {
  bearing,
  hasTerminalApproach,
  holePoints,
  legsFor,
  resolveTraceFinish,
  shotsForHole,
  type TraceFinish,
} from './holeProjection'
import { mapRotationFor } from './mapRotation'
import { traceInitial } from '../components/golf/trace'

/**
 * What goes on the club's live hole map — decided without a map.
 *
 * PURE. No React, no DOM, and above all no `ol`: the component that draws this
 * (`components/golf/HoleMap.tsx`) needs OpenLayers, a sized container and a
 * rendered frame, and none of those can answer whether a putt got drawn as a
 * shot. Everything below is arithmetic over coordinates the server sent, so
 * every rule the drawing has to obey is a test that runs in milliseconds.
 *
 * ## This file MEASURES NOTHING
 *
 * Every yardage here comes from `legsFor` in `holeProjection.ts`, which is
 * itself a port of `Golf::Tracking.legs` and the phone's `legsFor`, and whose
 * header binds the three: *"a change to either shape belongs in both files or
 * in neither."* A map that re-derived a distance from a screen coordinate — or
 * from the tile projection it happens to be drawn in — would be a FOURTH
 * answer to a question the club already answers, and the first place a member
 * would find the phone and the round page disagreeing.
 *
 * The same goes for the walk itself: which strike points get drawn is
 * `legsFor`'s prefix rule, not a filter written here. A saved first-putt origin
 * may close the final approach line, but it stays an unmarked endpoint; a
 * legacy stroke played from inside the green ends the ordinary prefix. Saved
 * positive putts may extend the visible line to the mapped pin, but never add
 * a leg, mark, club, distance or pill.
 *
 * ## What it does NOT hold, deliberately
 *
 * No hazards and no yardage labels beyond the legs. The paper plan
 * (`HolePlan.tsx`) draws bunkers because it is an engraving with the whole
 * hole in frame at a fixed size; a live map already shows the sand, in colour,
 * under the trace. Adding either here means adding it when something needs it.
 */

// ── The shapes a map is drawn from ─────────────────────────────────────────

/** One card, flattened to what a map needs: a seat, a name, and every shot they played. */
export interface HoleMapPlayer {
  id: string
  position: number
  displayName: string
  /** FLAT across every hole, as the wire sends them — filtering is this file's job. */
  shots: readonly Shot[]
  /** The whole score classifies a recorded, mapped-center or partial finish. */
  scores?: readonly HoleScore[]
}

/** A drawn strike point: where the member stood, and the letter inside the ring. */
export interface HoleMapMark {
  point: LatLng
  initial: string
}

/** One played leg, including the originating shot that owns its pill. */
export interface HoleMapLeg {
  /** WHOLE yards, exactly as `Golf::Tracking.trace` would quote them. */
  yards: number
  /** The club used for this leg. Null renders a distance-only pill. */
  club: string | null
  /** The real Shot coordinate where this leg and its pill begin. */
  from: LatLng
  /** The next known strike point, or the confirmed first-putt endpoint. */
  to: LatLng
}

export interface HoleMapTrace {
  playerId: string
  /** 1-4 — the seat the tone and dash come off (`components/golf/trace.ts`). */
  position: number
  displayName: string
  /**
   * ONE polyline through every known point, in order — real strike marks plus
   * an optional unmarked recorded or mapped endpoint. It is empty without a
   * leg or mapped-center finish; a one-point line otherwise renders as a blob
   * that reads as a shot going nowhere.
   */
  line: LatLng[]
  marks: HoleMapMark[]
  legs: HoleMapLeg[]
  finish: TraceFinish
}

/** The corner-to-corner box a view has to hold. `[lat, lng]`, like everything else here. */
export interface HoleMapExtent {
  minLat: number
  minLng: number
  maxLat: number
  maxLng: number
}

export interface HoleMapSpec {
  hole: number
  green: LatLng[]
  path: LatLng[]
  tee: LatLng
  pin: LatLng
  traces: HoleMapTrace[]
  view: {
    extent: HoleMapExtent
    rotation: number
    padding: [number, number, number, number]
  }
}

/**
 * Room for a strike ring, a yardage pill and the attribution to sit inside the
 * frame rather than half-clipped by its edge. The same four the geometry
 * editor fits with, in `View#fit`'s own top-right-bottom-left order.
 */
export const HOLE_MAP_PADDING: [number, number, number, number] = [44, 107, 44, 107]

// ── The build ──────────────────────────────────────────────────────────────

export function buildHoleMap(args: {
  hole: HoleGeometry
  scale: [number, number]
  players: readonly HoleMapPlayer[]
}): HoleMapSpec {
  const { hole, scale, players } = args

  const played = players.map((player) => ({
    player,
    shots: shotsForHole(player.shots, hole.number),
    score: player.scores?.find((score) => score.hole_number === hole.number),
  }))
  const finishPoints: LatLng[] = played.flatMap((entry) => {
    const endpoint = resolveTraceFinish(entry.shots, hole, entry.score).endpoint
    return endpoint === null ? [] : [endpoint]
  })

  return {
    hole: hole.number,
    tee: hole.tee,
    pin: hole.pin,
    path: hole.path,
    green: hole.green,
    traces: played
      // A player with no shots on this hole is not an empty trace — it is no
      // trace, and a legend built from these must not name them either.
      .filter((entry) => entry.shots.length > 0)
      .sort((a, b) => a.player.position - b.player.position)
      .map((entry) => traceFor(entry.player, entry.shots, hole, scale, entry.score)),
    view: {
      extent: extentOf([
        ...holePoints(hole),
        // EVERY strike point, not only the drawn prefix, plus each applicable
        // finish endpoint. Framing only the marked points can crop the one
        // unmarked point that closes the approach.
        ...played.flatMap((entry) => entry.shots.map((shot): LatLng => [shot.lat, shot.lng])),
        ...finishPoints,
      ], hole.tee),
      rotation: rotationFor(hole, scale),
      // A COPY. `HOLE_MAP_PADDING` is one array shared by every spec this
      // module builds; handing it out directly lets one caller's adjustment
      // reach all of them.
      padding: [...HOLE_MAP_PADDING],
    },
  }
}

/** How the client is being asked to fetch the imagery, and from where. */
export type PlateSource =
  | { kind: 'xyz'; url: string }
  | { kind: 'arcgis_rest'; url: string }

/**
 * WHICH KIND OF LAYER this plate wants, or null where there is nothing to
 * draw.
 *
 * The plate names a service, never a credential: New York State's aerial
 * survey is free and keyless, so there is nothing to attach to a url and
 * nothing to keep out of a log. `arcgis_rest` is a dynamic MapServer ROOT —
 * the client asks for an export per view — and `xyz` is a cached tile
 * template. Both shapes are understood because which one the club shows is a
 * licensing decision somebody may remake in a commit.
 *
 * Null is the ORDINARY answer, not a failure: a payload from before the club
 * served a plate, or a plate naming a shape this client cannot build. Either
 * way the caller draws the paper plan, which is a picture of the same hole.
 */
export function plateSource(plate: PlateImagery | null | undefined): PlateSource | null {
  if (!plate || !plate.url) return null
  if (plate.kind === 'xyz' || plate.kind === 'arcgis_rest') return { kind: plate.kind, url: plate.url }

  return null
}

/**
 * WHAT THE PICTURE SAYS, in words — the map's text alternative.
 *
 * The engraved plan carried a `<desc>` (`HolePlan`'s `descriptionFor`); the
 * live map that replaced it draws to a CANVAS, which announces nothing at all.
 * Most of what it draws is duplicated in HTML beside it — the legend names
 * every traced player, the card above carries every stroke — but the LEG
 * YARDAGES are not: they are painted on the canvas and exist nowhere else on
 * the round page. So this sentence is the only place a member who cannot see
 * the picture can get them.
 *
 * Names only recorded shots and measured legs, plus the explicit line-only
 * inference that positive putts can finish at the mapped green center. A hole
 * nobody took a shot on says so rather than describing an empty picture as a
 * round; a recorded green finish and a mapped-center finish remain distinct.
 */
export function describeHoleMap(spec: HoleMapSpec): string {
  if (spec.traces.length === 0) return 'No shots were recorded on this hole.'

  return `Shot traces. ${spec.traces.map(describeTrace).join(' ')}`
}

// ── Internals ──────────────────────────────────────────────────────────────

function describeTrace(trace: HoleMapTrace): string {
  const shots = `${trace.marks.length} ${trace.marks.length === 1 ? 'shot' : 'shots'} drawn`
  const legs = trace.legs.length === 0 ? '' : `, ${describeLegs(trace.legs)}`
  const finish =
    trace.finish === 'recorded'
      ? '; trace reaches the green'
      : trace.finish === 'mapped-center'
        ? '; trace reaches the mapped green center'
        : '; tracking stops at the last recorded position'

  return `${trace.displayName}: ${shots}${legs}${finish}.`
}

/** "leg D 231 yards", or a club-and-distance list — spoken, not tabulated. */
function describeLegs(legs: HoleMapLeg[]): string {
  const labels = legs.map((leg) => `${leg.club === null ? '' : `${leg.club} `}${leg.yards} yards`)
  const listed =
    labels.length === 1 ? labels[0]! : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)!}`

  return `${legs.length === 1 ? 'leg' : 'legs'} ${listed}`
}

/**
 * One player's hole, ready to draw.
 *
 * The marks are the walk's real-Shot PREFIX. Ordinarily there are
 * `legs.length + 1`; a terminal first-putt leg has no far-end mark, so there
 * are `legs.length`. This is `holeTrace`'s shape in `holeProjection.ts`
 * without the projection: the map has its own.
 */
function traceFor(
  player: HoleMapPlayer,
  shots: readonly Shot[],
  hole: HoleGeometry,
  scale: [number, number],
  score: HoleScore | undefined,
): HoleMapTrace {
  const firstPuttOrigin = score?.first_putt_origin ?? null
  const legs = legsFor(shots, hole, scale, firstPuttOrigin)
  const hasTerminalLeg = hasTerminalApproach(shots, hole, firstPuttOrigin)
  const resolvedFinish = resolveTraceFinish(shots, hole, score)
  const drawn = shots.slice(0, Math.min(legs.length + 1 - (hasTerminalLeg ? 1 : 0), shots.length))
  const initial = traceInitial(player.displayName)
  const marks = drawn.map((shot): HoleMapMark => ({ point: [shot.lat, shot.lng], initial }))
  const measuredLine: LatLng[] = legs.length > 0 ? [legs[0]!.from, ...legs.map((leg) => leg.to)] : []
  const line =
    resolvedFinish.finish === 'mapped-center' && resolvedFinish.endpoint !== null
      ? [
          ...(measuredLine.length > 0
            ? measuredLine
            : [[shots[0]!.lat, shots[0]!.lng] satisfies LatLng]),
          resolvedFinish.endpoint,
        ]
      : measuredLine

  return {
    playerId: player.id,
    position: player.position,
    displayName: player.displayName,
    line,
    marks,
    legs: legs.map((leg) => ({
      yards: leg.yards,
      club: leg.club,
      from: leg.from,
      to: leg.to,
    })),
    finish: resolvedFinish.finish,
  }
}

/**
 * ── THE SIGN, WRITTEN DOWN ─────────────────────────────────────────────────
 *
 * `mapRotationFor` answers "the view rotation that points a hole of this
 * bearing UP the screen", and it NEGATES INTERNALLY — pass the compass
 * bearing, never its negative. Passing `-bearing` returns `+bearing`, which
 * draws every hole upside down: a picture, not an error, and therefore the
 * kind of mistake that survives a reading.
 *
 * `courseGeometryGeo.test.ts` pins the convention against OpenLayers' own
 * coordinate-to-pixel transform, and `MapCanvas.tsx` — the other OpenLayers
 * surface — passes `holeBearing` unnegated for the same reason.
 *
 * `bearing()` is degrees clockwise from north; `mapRotationFor` takes radians.
 */
function rotationFor(hole: HoleGeometry, scale: [number, number]): number {
  return mapRotationFor((bearing(hole.tee, hole.pin, scale) * Math.PI) / 180)
}

/**
 * The corner-to-corner box those points fall in.
 *
 * Folded rather than spread through `Math.min`: a traced course's outline runs
 * to thousands of vertices, and `Math.min(...points)` passes every one as an
 * argument, which blows the stack somewhere over a hundred thousand. `fallback`
 * is the tee, following `projectHole`'s own degenerate case — a hole whose
 * every coordinate is unusable has no extent to compute, and inventing one
 * would frame a course somewhere in the Atlantic.
 */
function extentOf(points: readonly LatLng[], fallback: LatLng): HoleMapExtent {
  const usable = points.length > 0 ? points : [fallback]
  const first = usable[0]!
  const extent: HoleMapExtent = {
    minLat: first[0],
    minLng: first[1],
    maxLat: first[0],
    maxLng: first[1],
  }

  for (const [lat, lng] of usable) {
    if (lat < extent.minLat) extent.minLat = lat
    if (lat > extent.maxLat) extent.maxLat = lat
    if (lng < extent.minLng) extent.minLng = lng
    if (lng > extent.maxLng) extent.maxLng = lng
  }
  return extent
}
